'use client';
import { useEffect } from 'react';
import { useUserProfile } from '../api';
import { useBoundStore } from '../stores/store';
import { Record } from 'iconsax-reactjs';
import { apiFetch } from '../apiFetch';
import { v4 } from 'uuid';
import { useLocale } from 'next-intl';
import { isMobileDevice, logMessage } from '../utils';
import { getQueryClient } from '../queryClientSingleton';
import type { User } from '@/modules/authentication/types';
import type { IPLocation } from '@/modules/onboarding/utils';

const shouldTrack = () =>
  process.env.NEXT_PUBLIC_ENV === 'production' ||
  process.env.NEXT_PUBLIC_ENV === 'testing' ||  process.env.NEXT_PUBLIC_ENV === 'development' || process.env.NEXT_PUBLIC_ENV === 'staging';

const getAnonymousId = () => {
  if (typeof window === 'undefined') return undefined;
  let id = localStorage.getItem('anonymousId');
  if (!id) {
    id = v4();
    localStorage.setItem('anonymousId', id);
  }
  return id;
};

// Profile/IP are resolved from the React Query cache so analytics can be fired
// outside of React (socket handlers, store actions) as well as inside the hook.
const resolveProfile = () =>
  getQueryClient()?.getQueryData<User>(['userProfile']);

const resolveIP = async () => {
  const qc = getQueryClient();
  const cached = qc?.getQueryData<IPLocation>(['getIP']);
  if (cached) return cached;
  try {
    const data = await apiFetch<IPLocation>('/api/v1/ip-location', {
      method: 'GET',
    });
    qc?.setQueryData(['getIP'], data);
    return data;
  } catch {
    return undefined;
  }
};

const getEventInfo = (profile?: User) => ({
  messageId: v4(),
  timestamp: new Date().toISOString(),
  userId: profile?.id ? profile.id.toString() : undefined,
  anonymousId: getAnonymousId(),
});

const getTraits = (profile?: User) =>
  profile
    ? {
        name: `${profile.firstName} ${profile.lastName}`,
        email: profile.email,
        subscription: profile.lastActiveAccount.subscription?.name,
        role: profile.accountRole.name,
        account_id: profile.lastActiveAccountId,
        spaceName: profile.lastActiveAccount.name,
        teamName: profile.lastActiveAccount.teamName,
        farmName: profile.lastActiveProperty?.name,
        farmLocation: profile.lastActiveProperty?.location?.coordinates,
      }
    : undefined;

const getPageProps = () => ({
  path: window.location.pathname,
  referrer: document.referrer,
  search: window.location.search,
  title: document.title,
  url: window.location.href,
});

const getContext = async (locale?: string) => {
  const data = await resolveIP();
  return {
    timezone: data?.time_zone.id ?? undefined,
    ip: data?.ip,
    userAgent: navigator.userAgent,
    page: getPageProps(),
    locale: locale ?? document.documentElement.lang ?? undefined,
  };
};

const endpointFor = (profile?: User) =>
  profile ? '/api/v1/sns/analytics' : '/api/v1/sns/identify';

/**
 * Fire a `track` analytics event from anywhere — React or not. Resolves the
 * current profile/IP from the query cache, so callers don't need hook context
 * (e.g. socket event handlers that set limit-reached flags).
 */
export const trackEvent = async (
  event: string,
  data?: Record<string, unknown>,
  locale?: string
) => {
  if (!shouldTrack()) return;
  const profile = resolveProfile();
  const platform = isMobileDevice() ? 'orth_web_mobile' : 'orth_web';
  const context = await getContext(locale);
  return await apiFetch(endpointFor(profile), {
    method: 'POST',
    body: {
      type: 'track',
      event,
      ...getEventInfo(profile),
      properties: {
        ...getTraits(profile),
        ...data,
        platform,
      },
      context,
    },
  });
};

export const useAnalytics = () => {
  const { accessToken } = useBoundStore();
  const { data: profile } = useUserProfile(!!accessToken);
  const platform = isMobileDevice() ? 'orth_web_mobile' : 'orth_web';

  const locale = useLocale();

  const track = (event: string, data?: Record<string, unknown>) =>
    trackEvent(event, data, locale);

  const page = async (name: string) => {
    if (!shouldTrack()) return;
    const context = await getContext(locale);
    return await apiFetch(endpointFor(profile), {
      method: 'POST',
      body: {
        type: 'page',
        name,
        ...getEventInfo(profile),
        properties: {
          name,
          ...getTraits(profile),
          ...getPageProps(),
          platform,
        },
        context,
      },
    });
  };

  const identify = async ({
    userId,
    traits,
  }: {
    userId: number;
    traits?: Record<string, unknown>;
  }) => {
    if (!shouldTrack()) return;
    const context = await getContext(locale);

    const identifyData = {
      type: 'identify',
      ...getEventInfo(profile),
      userId: userId.toString(),
      traits: { ...getTraits(profile), ...traits },
      context,
      properties: {
        platform,
      },
    };

    logMessage('info', 'Analytics Identify Payload:', identifyData);

    return await apiFetch(endpointFor(profile), {
      method: 'POST',
      body: identifyData,
    });
  };

  useEffect(() => {
    const persisted = localStorage.getItem('anonymousId');
    if (!persisted) {
      localStorage.setItem('anonymousId', v4());
    }
  }, []);

  useEffect(() => {
    const identified = sessionStorage.getItem('identified');
    if (!!profile && !identified) {
      identify({
        userId: profile.id,
      });
      sessionStorage.setItem('identified', 'true');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  return { track, identify, page, identifiable: !!profile };
};
